/**
 * <header-menu>: desktop dropdowns and the mega menu (disclosure pattern).
 * Opens on click and keyboard everywhere, and on hover for mouse users.
 * Closes on Escape, outside click, or when focus leaves the menu.
 */
if (!customElements.get('header-menu')) {
  class HeaderMenu extends HTMLElement {
    static HOVER_CLOSE_DELAY = 150;

    connectedCallback() {
      this.triggers = Array.from(this.querySelectorAll('[data-nav-trigger]'));
      this.canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      this.current = null;
      this.openedByHover = false;

      this.triggers.forEach((trigger) => {
        const item = trigger.closest('li');
        trigger.addEventListener('click', () => this.onClick(trigger));

        if (this.canHover) {
          item.addEventListener('mouseenter', () => {
            clearTimeout(this.closeTimer);
            if (this.current !== trigger) {
              this.open(trigger);
              this.openedByHover = true;
            }
          });
          item.addEventListener('mouseleave', () => {
            this.closeTimer = setTimeout(() => this.close(trigger), HeaderMenu.HOVER_CLOSE_DELAY);
          });
        }
      });

      this.onKeydown = (event) => {
        if (event.key !== 'Escape' || !this.current) return;
        const trigger = this.current;
        this.close(trigger);
        trigger.focus();
      };
      this.onFocusOut = (event) => {
        if (!this.contains(event.relatedTarget)) this.closeAll();
      };
      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.closeAll();
      };

      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('click', this.onDocumentClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      clearTimeout(this.closeTimer);
    }

    onClick(trigger) {
      // A hover already opened this panel: the click confirms it instead of closing it.
      if (this.current === trigger && this.openedByHover) {
        this.openedByHover = false;
        return;
      }
      this.current === trigger ? this.close(trigger) : this.open(trigger);
      this.openedByHover = false;
    }

    panelFor(trigger) {
      return document.getElementById(trigger.getAttribute('aria-controls'));
    }

    open(trigger) {
      if (this.current && this.current !== trigger) this.close(this.current);
      trigger.setAttribute('aria-expanded', 'true');
      this.panelFor(trigger).hidden = false;
      this.current = trigger;
    }

    close(trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      this.panelFor(trigger).hidden = true;
      if (this.current === trigger) {
        this.current = null;
        this.openedByHover = false;
      }
    }

    closeAll() {
      if (this.current) this.close(this.current);
    }
  }

  customElements.define('header-menu', HeaderMenu);
}

/**
 * Drawer accordions: <details data-accordion> animate their height open and closed.
 * Without JS they still work as plain <details>. Reduced motion: no height animation.
 */
(() => {
  const EASE = 'cubic-bezier(0.23, 1, 0.32, 1)';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const toggle = (details) => {
    const summary = details.querySelector('summary');
    // The intended state, not details.open: a closing animation keeps the element open until it ends.
    const closing = (details.dataset.state || (details.open ? 'open' : 'closed')) === 'open';
    details.dataset.state = closing ? 'closed' : 'open';

    if (reduceMotion.matches || !details.animate) {
      details.open = !closing;
      return;
    }

    details.currentAnimation?.cancel();
    const start = details.offsetHeight;
    if (!closing) details.open = true;
    const end = closing ? summary.offsetHeight : details.scrollHeight;

    details.style.overflow = 'hidden';
    const animation = details.animate(
      { height: [`${start}px`, `${end}px`] },
      { duration: closing ? 200 : 300, easing: EASE }
    );
    details.currentAnimation = animation;
    animation.onfinish = () => {
      if (closing) details.open = false;
      details.style.overflow = '';
      details.currentAnimation = null;
    };
  };

  document.addEventListener('click', (event) => {
    const summary = event.target.closest('details[data-accordion] > summary');
    if (!summary) return;
    event.preventDefault();
    toggle(summary.parentElement);
  });
})();
